import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { format } from "date-fns";
import { sv } from "date-fns/locale";
import { Bell, Plus, Trash2, Send, ExternalLink, Users, Search, CheckCircle2, Clock, Eye } from "lucide-react";
import type { Notification, Client } from "@shared/schema";

type NotificationWithCounts = Notification & { sentCount: number; readCount: number };

type NotificationReceipt = {
  clientId: string;
  clientName: string;
  readAt: string | null;
  createdAt: string | null;
};

export default function AdminNotifications() {
  const { toast } = useToast();
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [link, setLink] = useState("");
  const [sendToAll, setSendToAll] = useState(true);
  const [selectedClientIds, setSelectedClientIds] = useState<Set<string>>(new Set());
  const [clientSearch, setClientSearch] = useState("");
  const [detailNotif, setDetailNotif] = useState<NotificationWithCounts | null>(null);

  const { data: notifications, isLoading } = useQuery<NotificationWithCounts[]>({
    queryKey: ["/api/admin/notifications"],
  });

  const { data: clients } = useQuery<Client[]>({
    queryKey: ["/api/admin/clients"],
  });

  const { data: receipts, isLoading: receiptsLoading } = useQuery<NotificationReceipt[]>({
    queryKey: ["/api/admin/notifications", detailNotif?.id ?? "__none__", "receipts"],
    enabled: !!detailNotif,
  });

  const filteredClients = (clients || []).filter(c =>
    c.name.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const toggleClient = (id: string) => {
    setSelectedClientIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    const allSelected = filteredClients.every(c => selectedClientIds.has(c.id));
    if (allSelected) {
      setSelectedClientIds(prev => {
        const next = new Set(prev);
        filteredClients.forEach(c => next.delete(c.id));
        return next;
      });
    } else {
      setSelectedClientIds(prev => {
        const next = new Set(prev);
        filteredClients.forEach(c => next.add(c.id));
        return next;
      });
    }
  };

  const resetForm = () => {
    setTitle("");
    setBody("");
    setLink("");
    setSendToAll(true);
    setSelectedClientIds(new Set());
    setClientSearch("");
  };

  const createMutation = useMutation({
    mutationFn: async () => {
      const payload: any = { title, body, link: link || null };
      if (!sendToAll && selectedClientIds.size > 0) {
        payload.clientIds = Array.from(selectedClientIds);
      }
      return apiRequest("POST", "/api/admin/notifications", payload);
    },
    onSuccess: () => {
      const count = sendToAll ? (clients?.length || 0) : selectedClientIds.size;
      toast({ title: `Notifikation skickad till ${count} kund${count !== 1 ? "er" : ""}!` });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/notifications"] });
      setCreateDialogOpen(false);
      resetForm();
    },
    onError: () => {
      toast({ title: "Fel", description: "Kunde inte skicka notifikation", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiRequest("DELETE", `/api/admin/notifications/${id}`),
    onSuccess: () => {
      toast({ title: "Notifikation raderad" });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/notifications"] });
    },
  });

  const canSend = title.trim() && body.trim() && (sendToAll || selectedClientIds.size > 0);

  const readReceipts = receipts?.filter(r => r.readAt) || [];
  const unreadReceipts = receipts?.filter(r => !r.readAt) || [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Notifikationer</h1>
        <p className="text-muted-foreground">Skicka meddelanden till alla eller utvalda kunder</p>
      </div>

      <Card>
        <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Bell className="h-5 w-5" />
              Skickade notifikationer
            </CardTitle>
            <CardDescription>Kampanjer och meddelanden till kunder</CardDescription>
          </div>
          <Button onClick={() => setCreateDialogOpen(true)} data-testid="button-create-notification">
            <Plus className="h-4 w-4 mr-2" />
            Ny notifikation
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}
            </div>
          ) : notifications && notifications.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Titel</TableHead>
                    <TableHead>Meddelande</TableHead>
                    <TableHead>Länk</TableHead>
                    <TableHead>Datum</TableHead>
                    <TableHead>
                      <span className="flex items-center gap-1">
                        <Users className="h-3.5 w-3.5" />
                        Skickad till
                      </span>
                    </TableHead>
                    <TableHead>
                      <span className="flex items-center gap-1">
                        <Eye className="h-3.5 w-3.5" />
                        Läste
                      </span>
                    </TableHead>
                    <TableHead className="text-right">Radera</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {notifications.map((notif) => (
                    <TableRow key={notif.id}>
                      <TableCell className="font-medium">{notif.title}</TableCell>
                      <TableCell className="max-w-[260px] text-sm text-muted-foreground">
                        <p className="line-clamp-2 whitespace-pre-wrap break-words">{notif.body}</p>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {notif.link ? (
                          <a
                            href={notif.link.startsWith("http") ? notif.link : undefined}
                            target={notif.link.startsWith("http") ? "_blank" : undefined}
                            rel={notif.link.startsWith("http") ? "noopener noreferrer" : undefined}
                            className="flex items-center gap-1 text-primary hover:underline"
                            data-testid={`link-notif-${notif.id}`}
                          >
                            <ExternalLink className="h-3 w-3" />
                            <span className="truncate max-w-[120px]">{notif.link}</span>
                          </a>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                        {format(new Date(notif.createdAt!), "d MMM yyyy HH:mm", { locale: sv })}
                      </TableCell>
                      <TableCell>
                        <button
                          className="flex items-center gap-1.5 group"
                          onClick={() => setDetailNotif(notif)}
                          data-testid={`button-receipts-${notif.id}`}
                          title="Visa mottagare"
                        >
                          <Badge variant="secondary" className="group-hover:bg-primary/10 group-hover:text-primary transition-colors cursor-pointer">
                            {notif.sentCount} kund{notif.sentCount !== 1 ? "er" : ""}
                          </Badge>
                        </button>
                      </TableCell>
                      <TableCell>
                        <button
                          className="flex items-center gap-1.5 group"
                          onClick={() => setDetailNotif(notif)}
                          data-testid={`button-read-${notif.id}`}
                          title="Visa vem som läst"
                        >
                          <Badge
                            variant={notif.readCount > 0 ? "default" : "outline"}
                            className="group-hover:opacity-80 transition-opacity cursor-pointer"
                          >
                            {notif.readCount} / {notif.sentCount}
                          </Badge>
                        </button>
                      </TableCell>
                      <TableCell className="text-right">
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button size="icon" variant="ghost" className="text-destructive" data-testid={`button-delete-notif-${notif.id}`}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Radera notifikation?</AlertDialogTitle>
                              <AlertDialogDescription>
                                Denna åtgärd kan inte ångras.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Avbryt</AlertDialogCancel>
                              <AlertDialogAction
                                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                onClick={() => deleteMutation.mutate(notif.id)}
                              >
                                Radera
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-12 text-center">
              <Bell className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <p className="text-muted-foreground">Inga notifikationer skickade ännu</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recipient detail sheet */}
      <Sheet open={!!detailNotif} onOpenChange={(open) => { if (!open) setDetailNotif(null); }}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader className="mb-4">
            <SheetTitle className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              Leveransinfo
            </SheetTitle>
            {detailNotif && (
              <SheetDescription>
                <span className="font-medium text-foreground">{detailNotif.title}</span>
                <br />
                <span className="text-xs">
                  Skickad {format(new Date(detailNotif.createdAt!), "d MMM yyyy HH:mm", { locale: sv })}
                </span>
              </SheetDescription>
            )}
          </SheetHeader>

          {detailNotif && (
            <div className="space-y-4">
              {/* Full message body */}
              <div className="rounded-lg bg-muted/50 p-3 text-sm whitespace-pre-wrap break-words">
                {detailNotif.body}
              </div>

              {/* Summary badges */}
              <div className="flex gap-3">
                <div className="flex-1 rounded-lg border p-3 text-center">
                  <p className="text-2xl font-bold">{detailNotif.sentCount}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Skickad till</p>
                </div>
                <div className="flex-1 rounded-lg border p-3 text-center">
                  <p className="text-2xl font-bold text-primary">{detailNotif.readCount}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Läste</p>
                </div>
                <div className="flex-1 rounded-lg border p-3 text-center">
                  <p className="text-2xl font-bold text-muted-foreground">{detailNotif.sentCount - detailNotif.readCount}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Ej läst</p>
                </div>
              </div>

              {receiptsLoading ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-10 w-full" />)}
                </div>
              ) : receipts && receipts.length > 0 ? (
                <div className="space-y-1" data-testid="receipts-list">
                  {/* Read recipients first */}
                  {readReceipts.length > 0 && (
                    <div>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-1 mb-1">Läst</p>
                      {readReceipts.map(r => (
                        <div
                          key={r.clientId}
                          className="flex items-center justify-between py-2.5 px-3 rounded-lg bg-primary/5"
                          data-testid={`receipt-read-${r.clientId}`}
                        >
                          <div className="flex items-center gap-2.5">
                            <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0" />
                            <span className="text-sm font-medium">{r.clientName}</span>
                          </div>
                          {r.readAt && (
                            <span className="text-xs text-muted-foreground whitespace-nowrap">
                              {format(new Date(r.readAt), "d MMM HH:mm", { locale: sv })}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Unread recipients */}
                  {unreadReceipts.length > 0 && (
                    <div className={readReceipts.length > 0 ? "mt-3" : ""}>
                      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide px-1 mb-1">Ej läst</p>
                      {unreadReceipts.map(r => (
                        <div
                          key={r.clientId}
                          className="flex items-center gap-2.5 py-2.5 px-3 rounded-lg"
                          data-testid={`receipt-unread-${r.clientId}`}
                        >
                          <Clock className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <span className="text-sm text-muted-foreground">{r.clientName}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="py-8 text-center text-muted-foreground text-sm">
                  Inga mottagare hittades
                </div>
              )}
            </div>
          )}
        </SheetContent>
      </Sheet>

      {/* Create notification dialog */}
      <Dialog open={createDialogOpen} onOpenChange={(open) => {
        setCreateDialogOpen(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ny notifikation</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium mb-1.5 block">Titel</label>
              <Input
                placeholder="Rubrik för notifikationen..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                data-testid="input-notif-title"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Meddelande</label>
              <Textarea
                placeholder="Skriv meddelandet till kunderna..."
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={4}
                data-testid="input-notif-body"
              />
            </div>
            <div>
              <label className="text-sm font-medium mb-1.5 block">Länk (valfritt)</label>
              <Input
                placeholder="https://..."
                value={link}
                onChange={(e) => setLink(e.target.value)}
                data-testid="input-notif-link"
              />
            </div>

            <div className="border-t pt-4">
              <label className="text-sm font-medium mb-3 block flex items-center gap-2">
                <Users className="h-4 w-4" />
                Mottagare
              </label>
              <div className="space-y-3">
                <div
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${sendToAll ? "bg-primary/5 border-primary/30" : "hover:bg-muted/50"}`}
                  onClick={() => { setSendToAll(true); setSelectedClientIds(new Set()); }}
                  data-testid="option-send-all"
                >
                  <Checkbox checked={sendToAll} onCheckedChange={() => { setSendToAll(true); setSelectedClientIds(new Set()); }} />
                  <div>
                    <p className="font-medium text-sm">Alla kunder</p>
                    <p className="text-xs text-muted-foreground">{clients?.length || 0} kunder</p>
                  </div>
                </div>
                <div
                  className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${!sendToAll ? "bg-primary/5 border-primary/30" : "hover:bg-muted/50"}`}
                  onClick={() => setSendToAll(false)}
                  data-testid="option-send-selected"
                >
                  <Checkbox checked={!sendToAll} onCheckedChange={() => setSendToAll(false)} />
                  <div>
                    <p className="font-medium text-sm">Utvalda kunder</p>
                    <p className="text-xs text-muted-foreground">
                      {!sendToAll && selectedClientIds.size > 0
                        ? `${selectedClientIds.size} vald${selectedClientIds.size !== 1 ? "a" : ""}`
                        : "Välj specifika kunder"}
                    </p>
                  </div>
                </div>

                {!sendToAll && (
                  <div className="border rounded-lg p-3 space-y-2">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <Input
                        placeholder="Sök kund..."
                        value={clientSearch}
                        onChange={(e) => setClientSearch(e.target.value)}
                        className="pl-9 h-8 text-sm"
                        data-testid="input-search-clients-notif"
                      />
                    </div>
                    {filteredClients.length > 1 && (
                      <div className="flex items-center justify-between px-1">
                        <button
                          type="button"
                          className="text-xs text-primary hover:underline"
                          onClick={toggleAllFiltered}
                          data-testid="button-toggle-all-clients"
                        >
                          {filteredClients.every(c => selectedClientIds.has(c.id)) ? "Avmarkera alla" : "Markera alla"}
                        </button>
                        <span className="text-xs text-muted-foreground">
                          {selectedClientIds.size} / {clients?.length || 0}
                        </span>
                      </div>
                    )}
                    <div className="max-h-48 overflow-y-auto space-y-1">
                      {filteredClients.length > 0 ? filteredClients.map(client => (
                        <label
                          key={client.id}
                          className={`flex items-center gap-3 p-2 rounded-md cursor-pointer text-sm transition-colors ${selectedClientIds.has(client.id) ? "bg-primary/5" : "hover:bg-muted/50"}`}
                          data-testid={`checkbox-client-${client.id}`}
                        >
                          <Checkbox
                            checked={selectedClientIds.has(client.id)}
                            onCheckedChange={() => toggleClient(client.id)}
                          />
                          <span>{client.name}</span>
                        </label>
                      )) : (
                        <p className="text-xs text-muted-foreground text-center py-2">Inga kunder hittades</p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCreateDialogOpen(false); resetForm(); }}>Avbryt</Button>
            <Button
              onClick={() => createMutation.mutate()}
              disabled={createMutation.isPending || !canSend}
              data-testid="button-send-notification"
            >
              <Send className="h-4 w-4 mr-2" />
              {createMutation.isPending
                ? "Skickar..."
                : sendToAll
                  ? `Skicka till alla (${clients?.length || 0})`
                  : `Skicka till ${selectedClientIds.size} kund${selectedClientIds.size !== 1 ? "er" : ""}`
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
